import React, { useState, useEffect } from "react";
import { Button, Input, Select } from "../../../components";
import type {
  LoadSource,
  LoadSourceEditInput,
  SourceType,
  NGTurbineType,
} from "../domain/types";

interface Props {
  isOpen: boolean;
  source: LoadSource | null;
  onClose: () => void;
  onSubmit: (sourceId: string, data: LoadSourceEditInput) => Promise<void>;
}

export function LoadEditSourceModal({ isOpen, source, onClose, onSubmit }: Props) {
  const [form, setForm] = useState<LoadSourceEditInput>({});
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (source) {
      setForm({
        source_name: source.source_name,
        nameplate_capacity_mw: source.nameplate_capacity_mw,
        tech_type_id: source.tech_type_id ?? undefined,
        ng_turbine_type: (source.ng_turbine_type as NGTurbineType) ?? undefined,
        ng_min_load_fraction: source.ng_min_load_fraction ?? undefined,
        ng_has_scr: source.ng_has_scr ?? undefined,
        ng_scr_nox_reduction_pct: source.ng_scr_nox_reduction_pct ?? undefined,
        bess_capacity_mwh: source.bess_capacity_mwh ?? undefined,
        bess_duration_hours: source.bess_duration_hours ?? undefined,
        bess_round_trip_eff: source.bess_round_trip_eff ?? undefined,
        is_active: source.is_active,
      });
      setError(null);
    }
  }, [source]);

  if (!isOpen || !source) return null;

  const set = (field: keyof LoadSourceEditInput, value: any) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      // Only send changed fields
      const patch: LoadSourceEditInput = {};
      if (form.source_name !== source.source_name) patch.source_name = form.source_name;
      if (form.nameplate_capacity_mw !== source.nameplate_capacity_mw)
        patch.nameplate_capacity_mw = form.nameplate_capacity_mw;
      if (form.tech_type_id !== (source.tech_type_id ?? undefined))
        patch.tech_type_id = form.tech_type_id;
      if (form.is_active !== source.is_active) patch.is_active = form.is_active;
      if (form.ng_turbine_type !== (source.ng_turbine_type ?? undefined))
        patch.ng_turbine_type = form.ng_turbine_type as NGTurbineType;
      if (form.ng_min_load_fraction !== (source.ng_min_load_fraction ?? undefined))
        patch.ng_min_load_fraction = form.ng_min_load_fraction;
      if (form.ng_has_scr !== (source.ng_has_scr ?? undefined))
        patch.ng_has_scr = form.ng_has_scr;
      if (form.ng_scr_nox_reduction_pct !== (source.ng_scr_nox_reduction_pct ?? undefined))
        patch.ng_scr_nox_reduction_pct = form.ng_scr_nox_reduction_pct;
      if (form.bess_capacity_mwh !== (source.bess_capacity_mwh ?? undefined))
        patch.bess_capacity_mwh = form.bess_capacity_mwh;
      if (form.bess_duration_hours !== (source.bess_duration_hours ?? undefined))
        patch.bess_duration_hours = form.bess_duration_hours;
      if (form.bess_round_trip_eff !== (source.bess_round_trip_eff ?? undefined))
        patch.bess_round_trip_eff = form.bess_round_trip_eff;

      if (Object.keys(patch).length === 0) {
        onClose();
        return;
      }

      await onSubmit(source.source_id, patch);
      onClose();
    } catch (err: any) {
      setError(err.message || "Failed to update source");
    } finally {
      setSubmitting(false);
    }
  };

  const sourceType = source.source_type as SourceType;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-surface-700 rounded-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-h4 text-text-primary mb-4">Edit Source</h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Source Name"
              value={form.source_name || ""}
              onChange={(e) => set("source_name", e.target.value)}
              required
            />

            <div className="grid grid-cols-2 gap-4">
              <Select
                label="Source Type"
                value={sourceType}
                onChange={() => {}}
                disabled
                options={[
                  { value: "solar", label: "Solar PV" },
                  { value: "wind", label: "Wind" },
                  { value: "ng", label: "Natural Gas" },
                  { value: "bess", label: "Battery Storage" },
                ]}
              />

              <Input
                label="Capacity (MW)"
                type="number"
                step="0.1"
                min="0.1"
                value={form.nameplate_capacity_mw ?? 0}
                onChange={(e) => {
                  const cap = parseFloat(e.target.value) || 0;
                  setForm((prev) => ({
                    ...prev,
                    nameplate_capacity_mw: cap,
                    ...(sourceType === "bess" && {
                      bess_capacity_mwh: cap * (prev.bess_duration_hours ?? 4),
                    }),
                  }));
                }}
                required
              />
            </div>

            {/* Solar/Wind fields */}
            {(sourceType === "solar" || sourceType === "wind") && (
              <Select
                label="Technology Type"
                value={
                  form.tech_type_id ||
                  (sourceType === "solar" ? "solar_ground" : "wind_onshore")
                }
                onChange={(e) => set("tech_type_id", e.target.value)}
                options={
                  sourceType === "solar"
                    ? [
                        { value: "solar_ground", label: "Ground-Mount Solar" },
                        { value: "solar_rooftop", label: "Rooftop Solar" },
                      ]
                    : [{ value: "wind_onshore", label: "Onshore Wind" }]
                }
              />
            )}

            {/* NG fields */}
            {sourceType === "ng" && (
              <>
                <Select
                  label="Turbine Type"
                  value={form.ng_turbine_type || "ccgt"}
                  onChange={(e) =>
                    set("ng_turbine_type", e.target.value as NGTurbineType)
                  }
                  options={[
                    { value: "ccgt", label: "Combined Cycle (CCGT)" },
                    { value: "scgt", label: "Simple Cycle (SCGT)" },
                    { value: "reciprocating", label: "Reciprocating Engine" },
                  ]}
                />
                <Input
                  label="Min Load Fraction"
                  type="number"
                  step="0.05"
                  min="0"
                  max="1"
                  value={form.ng_min_load_fraction ?? 0.3}
                  onChange={(e) =>
                    set("ng_min_load_fraction", parseFloat(e.target.value))
                  }
                />

                {/* SCR (Selective Catalytic Reduction) */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.ng_has_scr ?? false}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setForm((prev) => ({
                        ...prev,
                        ng_has_scr: checked,
                        ng_scr_nox_reduction_pct: checked ? (prev.ng_scr_nox_reduction_pct ?? 0.9) : undefined,
                      }));
                    }}
                    className="w-4 h-4 rounded border-border bg-surface-600 text-quantum-500 focus:ring-quantum-500"
                  />
                  <span className="text-body-sm text-text-primary">
                    SCR (Selective Catalytic Reduction)
                  </span>
                </label>

                {form.ng_has_scr && (
                  <div className="ml-6 space-y-2">
                    <Input
                      label="NOx Reduction Efficiency"
                      type="number"
                      step="0.01"
                      min="0.50"
                      max="0.99"
                      value={form.ng_scr_nox_reduction_pct ?? 0.9}
                      onChange={(e) =>
                        set("ng_scr_nox_reduction_pct", parseFloat(e.target.value))
                      }
                    />
                    <p className="text-body-sm text-text-muted">
                      Typical: 90% (0.90). Range: 50-99%.
                    </p>
                  </div>
                )}
              </>
            )}

            {/* BESS fields */}
            {sourceType === "bess" && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Duration (hours)"
                    type="number"
                    step="0.5"
                    min="0.5"
                    value={form.bess_duration_hours ?? 4}
                    onChange={(e) => {
                      const dur = parseFloat(e.target.value) || 4;
                      setForm((prev) => ({
                        ...prev,
                        bess_duration_hours: dur,
                        bess_capacity_mwh: (prev.nameplate_capacity_mw ?? 0) * dur,
                      }));
                    }}
                  />
                  <Input
                    label="Round-trip Eff"
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    value={form.bess_round_trip_eff ?? 0.85}
                    onChange={(e) =>
                      set("bess_round_trip_eff", parseFloat(e.target.value))
                    }
                  />
                </div>
                <div className="bg-surface-600 rounded-lg px-3 py-2 text-body-sm text-text-muted">
                  Storage:{" "}
                  <span className="text-text-primary font-medium">
                    {((form.nameplate_capacity_mw ?? 0) * (form.bess_duration_hours ?? 4)).toFixed(0)} MWh
                  </span>
                  <span className="ml-1">
                    ({form.nameplate_capacity_mw ?? 0} MW &times; {form.bess_duration_hours ?? 4}h)
                  </span>
                </div>
              </div>
            )}

            {/* Active toggle */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_active ?? true}
                onChange={(e) => set("is_active", e.target.checked)}
                className="w-4 h-4 rounded border-border bg-surface-600 text-quantum-500 focus:ring-quantum-500"
              />
              <span className="text-body-sm text-text-primary">
                Active (included in dispatch)
              </span>
            </label>

            {error && (
              <div className="text-red-400 text-body-sm">{error}</div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="ghost" onClick={onClose} type="button">
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={submitting}>
                {submitting ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
